import {
  BellRing,
  CalendarCheck2,
  CalendarClock,
  Check,
  ChevronDown,
  CircleAlert,
  CircleHelp,
  History,
  Megaphone,
  MessageCircleQuestion,
  MessageSquareReply,
  MessageSquareShare,
  MessageSquareText,
  PhoneMissed,
  RotateCw,
  Search,
  Sparkles,
  TriangleAlert,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Button } from '../../../ui/Button/Button'
import { Skeleton } from '../../../ui/Skeleton/Skeleton'
import type { SmsActivity } from '../sms-activity-contracts'
import type { SmsActivityRepository } from '../sms-activity-repository'
import { useSmsActivity } from '../use-sms-activity'
import styles from './SmsActivityView.module.css'

type AutomationType = SmsActivity['automationType']
type ProviderStatus = SmsActivity['providerStatus']

// What a text is for decides where it belongs: reminders announce a visit,
// follow-ups invite the patient to continue (a reply makes it a conversation,
// listed in Conversations), confirmations tell them what happened.
type AutomationCategory = 'REMINDER' | 'FOLLOW_UP' | 'CONFIRMATION' | 'OTHER'

const categories: Record<AutomationCategory, { label: string }> = {
  REMINDER: { label: 'Reminders' },
  FOLLOW_UP: { label: 'Follow-ups' },
  CONFIRMATION: { label: 'Confirmations' },
  OTHER: { label: 'Other' },
}

const categoryOrder = Object.keys(categories) as AutomationCategory[]

// One glyph per automation, so a day's messages scan by kind before reading;
// its category's tone tells reminders, follow-ups and confirmations apart.
const automations: Record<
  AutomationType,
  { label: string; icon: LucideIcon; category: AutomationCategory }
> = {
  APPOINTMENT_REMINDER: {
    label: 'Appointment Reminder',
    icon: BellRing,
    category: 'REMINDER',
  },
  MISSED_CALL_TEXTBACK: {
    label: 'Missed Call Follow-Up',
    icon: PhoneMissed,
    category: 'FOLLOW_UP',
  },
  VOICE_ABANDONED_RECOVERY: {
    label: 'Unfinished Booking Follow-Up',
    icon: CalendarClock,
    category: 'FOLLOW_UP',
  },
  UNANSWERED_SMS_FOLLOW_UP: {
    label: 'Unanswered Text Follow-Up',
    icon: MessageCircleQuestion,
    category: 'FOLLOW_UP',
  },
  VOICE_BOOKING_CONFIRMATION: {
    label: 'Booking Confirmation',
    icon: CalendarCheck2,
    category: 'CONFIRMATION',
  },
  VOICE_STAFF_REQUEST_ACKNOWLEDGEMENT: {
    label: 'Request Received',
    icon: MessageSquareShare,
    category: 'CONFIRMATION',
  },
  AI_SMS_REPLY: { label: 'AI Reply', icon: Sparkles, category: 'OTHER' },
  LEGACY_FOLLOW_UP: {
    label: 'Legacy Follow-Up',
    icon: History,
    category: 'OTHER',
  },
  LEGACY_APPOINTMENT_CONFIRMATION: {
    label: 'Legacy Appointment Confirmation',
    icon: History,
    category: 'OTHER',
  },
  LEGACY_CAMPAIGN: {
    label: 'Legacy Campaign',
    icon: Megaphone,
    category: 'OTHER',
  },
  UNKNOWN: {
    label: 'Other Automation',
    icon: MessageSquareText,
    category: 'OTHER',
  },
}

const automationOrder = Object.keys(automations) as AutomationType[]

// The type filter: a whole category, or one type within it.
type AutomationFilter =
  'ALL' | `CATEGORY:${AutomationCategory}` | AutomationType

function matchesAutomation(type: AutomationType, filter: AutomationFilter) {
  if (filter === 'ALL') return true
  if (filter.startsWith('CATEGORY:'))
    return `CATEGORY:${automations[type].category}` === filter
  return type === filter
}

// Provider outcomes, worded exactly as far as the evidence goes: "accepted"
// never claims the message reached the phone, and "unconfirmed" names no review
// that staff could carry out (2026-10-30).
export const messageStatuses: Record<
  ProviderStatus,
  {
    label: string
    detail: string
    tone: 'neutral' | 'warning' | 'danger'
    icon: LucideIcon
  }
> = {
  PROVIDER_ACCEPTED: {
    label: 'Accepted by provider',
    detail:
      'The SMS provider took the message. Final delivery to the phone is not confirmed.',
    tone: 'neutral',
    icon: Check,
  },
  RECONCILIATION_REQUIRED: {
    label: 'Unconfirmed',
    detail: 'Flio couldn’t confirm whether the SMS provider took this text.',
    tone: 'warning',
    icon: TriangleAlert,
  },
  FAILED: {
    label: 'Failed',
    detail: 'The message could not be sent.',
    tone: 'danger',
    icon: CircleAlert,
  },
  UNKNOWN: {
    label: 'Status unavailable',
    detail: 'No provider status is recorded for this message.',
    tone: 'neutral',
    icon: CircleHelp,
  },
}

const statusOrder: ProviderStatus[] = [
  'PROVIDER_ACCEPTED',
  'RECONCILIATION_REQUIRED',
  'FAILED',
  'UNKNOWN',
]

const isIssue = (status: ProviderStatus) =>
  status === 'FAILED' || status === 'RECONCILIATION_REQUIRED'

type StatusFilter = 'ALL' | 'ISSUES'

// The number a text went to is a contact, not necessarily a patient. Names
// come only from the server's Conversations identity of that number; a shared
// number names every validated patient and never one of them alone.
function recipient(contact: SmsActivity['contact']) {
  const identity = contact.identity
  if (identity && identity.kind !== 'UNMATCHED') {
    return {
      primary: contact.name,
      secondary: contact.phone,
      patients: identity.kind === 'SHARED_CONTACT' ? identity.patients : [],
    }
  }
  // Without a reliable association, the canonical number identifies them.
  const unnamed =
    (identity?.kind === 'UNMATCHED' || contact.name === 'Patient') &&
    contact.phone !== null
  return {
    primary: unnamed ? contact.phone! : contact.name,
    secondary: !unnamed && contact.name !== 'Patient' ? contact.phone : null,
    patients: [] as string[],
  }
}

function matchesSearch(activity: SmsActivity, search: string) {
  const query = search.trim().toLowerCase()
  if (!query) return true
  const digits = query.replace(/\D/g, '')
  return (
    activity.contact.name.toLowerCase().includes(query) ||
    (activity.contact.identity?.patients ?? []).some((name) =>
      name.toLowerCase().includes(query)
    ) ||
    activity.messageText.toLowerCase().includes(query) ||
    (digits.length >= 3 &&
      (activity.contact.phone ?? '').replace(/\D/g, '').includes(digits))
  )
}

const timeFormat = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' })
const fullFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'full',
  timeStyle: 'short',
})
const dayFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
})
const dayYearFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

function startOfDay(ms: number) {
  const day = new Date(ms)
  day.setHours(0, 0, 0, 0)
  return day.getTime()
}

function dayLabel(dayStart: number, now: number) {
  const today = startOfDay(now)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (dayStart === today) return 'Today'
  if (dayStart === yesterday.getTime()) return 'Yesterday'
  return new Date(dayStart).getFullYear() === new Date(now).getFullYear()
    ? dayFormat.format(dayStart)
    : dayYearFormat.format(dayStart)
}

// Newest first, as the server orders them, split into the viewer's days.
function groupByDay(activities: SmsActivity[]) {
  const groups: { dayStart: number; items: SmsActivity[] }[] = []
  for (const activity of activities) {
    const dayStart = startOfDay(activity.occurredAtMs)
    const last = groups[groups.length - 1]
    if (last?.dayStart === dayStart) last.items.push(activity)
    else groups.push({ dayStart, items: [activity] })
  }
  return groups
}

export function MessageStatusPill({ status }: { status: ProviderStatus }) {
  const meta = messageStatuses[status]
  const Icon = meta.icon
  return (
    <span
      className={`${styles.status} ${styles[meta.tone]}`}
      title={meta.detail}
    >
      <Icon size={12} strokeWidth={2.25} aria-hidden="true" />
      {meta.label}
      {status === 'PROVIDER_ACCEPTED' ? (
        <span className={styles.srOnly}> · final delivery not confirmed</span>
      ) : null}
    </span>
  )
}

// What each status means, said once instead of on every row.
export function MessageStatusGuide({ className }: { className?: string }) {
  const titleId = useId()
  return (
    <section
      className={[styles.guide, className].filter(Boolean).join(' ')}
      aria-labelledby={titleId}
    >
      <h2 id={titleId}>Message status</h2>
      <dl>
        {statusOrder.map((status) => (
          <div key={status}>
            <dt>
              <MessageStatusPill status={status} />
            </dt>
            <dd>{messageStatuses[status].detail}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function MessageText({ text }: { text: string }) {
  const id = useId()
  const paragraph = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  useLayoutEffect(() => {
    const element = paragraph.current
    if (!element || expanded) return
    const measure = () =>
      setOverflowing(element.scrollHeight - element.clientHeight > 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [expanded, text])
  return (
    <>
      <p
        id={id}
        ref={paragraph}
        className={
          expanded ? styles.message : `${styles.message} ${styles.clamped}`
        }
      >
        {text}
      </p>
      {overflowing || expanded ? (
        <button
          className={styles.messageToggle}
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show full message'}
        </button>
      ) : null}
    </>
  )
}

function ActivityItem({ activity }: { activity: SmsActivity }) {
  const automation = automations[activity.automationType]
  const Icon = automation.icon
  const who = recipient(activity.contact)
  return (
    <li className={styles.item} data-category={automation.category}>
      <span className={styles.typeIcon} aria-hidden="true">
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div className={styles.itemMain}>
        <div className={styles.itemHead}>
          <strong className={styles.recipient}>{who.primary}</strong>
          {who.secondary ? (
            <span className={styles.phone}>{who.secondary}</span>
          ) : null}
        </div>
        {who.patients.length > 0 ? (
          <p className={styles.people} title={who.patients.join(', ')}>
            <UsersRound size={13} strokeWidth={1.75} aria-hidden="true" />
            <span>
              <span className={styles.srOnly}>
                Patients sharing this number:{' '}
              </span>
              {who.patients.join(', ')}
            </span>
          </p>
        ) : null}
        <div className={styles.tags}>
          <span className={styles.automation}>{automation.label}</span>
          {activity.patientReplied ? (
            <span
              className={styles.replied}
              title="The patient replied, so the conversation is in Conversations."
            >
              <MessageSquareReply
                size={12}
                strokeWidth={2}
                aria-hidden="true"
              />
              Patient replied
            </span>
          ) : null}
        </div>
        <MessageText text={activity.messageText} />
      </div>
      <div className={styles.itemAside}>
        <time
          dateTime={new Date(activity.occurredAtMs).toISOString()}
          title={fullFormat.format(activity.occurredAtMs)}
        >
          {timeFormat.format(activity.occurredAtMs)}
        </time>
        <MessageStatusPill status={activity.providerStatus} />
      </div>
    </li>
  )
}

function StateBlock({
  icon: Icon,
  title,
  children,
  tone,
  role,
}: {
  icon: LucideIcon
  title: string
  children?: ReactNode
  tone?: 'danger'
  role?: 'alert'
}) {
  return (
    <div className={styles.state} data-tone={tone} role={role}>
      <span className={styles.stateIcon} aria-hidden="true">
        <Icon size={20} strokeWidth={1.75} />
      </span>
      <strong>{title}</strong>
      {children}
    </div>
  )
}

export function SmsActivityView({
  tenantId,
  repository,
  className,
}: {
  tenantId: string
  repository: SmsActivityRepository
  className?: string
}) {
  const query = useSmsActivity(repository, tenantId)
  const activities = useMemo(
    () => query.data?.pages.flatMap((page) => page.activities) ?? [],
    [query.data]
  )
  const searchId = useId()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [automationFilter, setAutomationFilter] =
    useState<AutomationFilter>('ALL')

  // Only categories and types present in the loaded messages are offered.
  const presentAutomations = useMemo(() => {
    const present = new Set(activities.map((item) => item.automationType))
    return categoryOrder
      .map((category) => ({
        category,
        types: automationOrder.filter(
          (type) => present.has(type) && automations[type].category === category
        ),
      }))
      .filter(({ types }) => types.length > 0)
  }, [activities])
  // A choice the loaded messages no longer contain is no choice at all.
  const activeAutomation =
    automationFilter === 'ALL' ||
    activities.some((item) =>
      matchesAutomation(item.automationType, automationFilter)
    )
      ? automationFilter
      : 'ALL'
  const issueCount = activities.filter((item) =>
    isIssue(item.providerStatus)
  ).length
  const filtersActive =
    search.trim() !== '' || statusFilter !== 'ALL' || activeAutomation !== 'ALL'
  const visible = activities.filter(
    (item) =>
      (statusFilter === 'ALL' || isIssue(item.providerStatus)) &&
      matchesAutomation(item.automationType, activeAutomation) &&
      matchesSearch(item, search)
  )
  const groups = groupByDay(visible)
  const now = Date.now()
  const clearFilters = () => {
    setSearch('')
    setStatusFilter('ALL')
    setAutomationFilter('ALL')
  }

  return (
    <section
      className={[styles.panel, className].filter(Boolean).join(' ')}
      aria-labelledby="sms-activity-title"
    >
      <header className={styles.panelHeader}>
        <span className={styles.sectionLabel}>Sent by Flio</span>
        <h2 id="sms-activity-title">Automated messages</h2>
      </header>

      {activities.length > 0 ? (
        <div className={styles.toolbar}>
          <div className={styles.searchField}>
            <label className={styles.srOnly} htmlFor={searchId}>
              Search loaded messages
            </label>
            <Search size={16} aria-hidden="true" />
            <input
              id={searchId}
              type="search"
              value={search}
              placeholder="Name, phone or message"
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button
                type="button"
                aria-label="Clear message search"
                onClick={() => setSearch('')}
              >
                <X size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <div
            className={styles.segmented}
            role="group"
            aria-label="Filter by status"
          >
            <button
              type="button"
              aria-pressed={statusFilter === 'ALL'}
              onClick={() => setStatusFilter('ALL')}
            >
              All
            </button>
            <button
              type="button"
              aria-pressed={statusFilter === 'ISSUES'}
              onClick={() => setStatusFilter('ISSUES')}
            >
              Issues
              {issueCount > 0 ? (
                <span className={styles.count}>{issueCount}</span>
              ) : null}
            </button>
          </div>
          <div className={styles.selectField}>
            <label className={styles.srOnly} htmlFor={`${searchId}-type`}>
              Automation
            </label>
            <select
              id={`${searchId}-type`}
              value={activeAutomation}
              onChange={(event) =>
                setAutomationFilter(event.target.value as AutomationFilter)
              }
            >
              <option value="ALL">All automations</option>
              {presentAutomations.map(({ category, types }) => (
                <optgroup key={category} label={categories[category].label}>
                  {types.length > 1 ? (
                    <option value={`CATEGORY:${category}`}>
                      {`All ${categories[category].label.toLowerCase()}`}
                    </option>
                  ) : null}
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {automations[type].label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <ChevronDown size={14} aria-hidden="true" />
          </div>
        </div>
      ) : null}

      <div className={styles.body}>
        {query.isPending ? (
          <div className={styles.loading} role="status">
            <span className={styles.srOnly}>Loading SMS activity…</span>
            {[0, 1, 2, 3].map((row) => (
              <div className={styles.skeletonRow} key={row}>
                <Skeleton width="2.25rem" height="2.25rem" />
                <div>
                  <Skeleton width="40%" height="0.875rem" />
                  <Skeleton width="85%" height="0.75rem" />
                </div>
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <StateBlock
            icon={CircleAlert}
            title="SMS activity is unavailable"
            tone="danger"
            role="alert"
          >
            <p>No message data was changed.</p>
            <Button
              variant="secondary"
              type="button"
              onClick={() => void query.refetch()}
            >
              <RotateCw size={14} aria-hidden="true" /> Try again
            </Button>
          </StateBlock>
        ) : activities.length === 0 ? (
          <StateBlock
            icon={MessageSquareText}
            title="No automated messages yet"
          >
            <p>
              Reminders, follow-ups and confirmations that Flio texts to
              patients appear here. When a patient replies, the conversation
              appears in Conversations.
            </p>
          </StateBlock>
        ) : groups.length === 0 ? (
          <StateBlock icon={Search} title="No loaded messages match">
            <p>
              {query.hasNextPage
                ? 'Only loaded messages are searched. Clear the filters or load older activity.'
                : 'Try another search or clear the filters.'}
            </p>
            <Button variant="secondary" type="button" onClick={clearFilters}>
              Clear filters
            </Button>
          </StateBlock>
        ) : (
          groups.map((group) => (
            <section className={styles.day} key={group.dayStart}>
              <h3 className={styles.dayLabel}>
                {dayLabel(group.dayStart, now)}
              </h3>
              <ul className={styles.list}>
                {group.items.map((activity) => (
                  <ActivityItem key={activity.activityId} activity={activity} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      {activities.length > 0 ? (
        <footer className={styles.panelFooter}>
          <span className={styles.footerCount}>
            {filtersActive
              ? `${visible.length} of ${activities.length} loaded messages`
              : `${activities.length}${query.hasNextPage ? ' most recent' : ''} ${activities.length === 1 ? 'message' : 'messages'}`}
          </span>
          {query.isFetchNextPageError ? (
            <span className={styles.footerError} role="alert">
              Older activity couldn’t be loaded.
            </span>
          ) : null}
          {query.hasNextPage ? (
            <Button
              className={styles.footerButton}
              variant="secondary"
              type="button"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage
                ? 'Loading…'
                : query.isFetchNextPageError
                  ? 'Try again'
                  : 'Load older activity'}
            </Button>
          ) : null}
        </footer>
      ) : null}
    </section>
  )
}
